import axios, { AxiosError } from "axios";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:3002/api/v1";

/**
 * Base axios instance for backend communication.
 * Uses NEXT_PUBLIC_API_URL env variable with localhost fallback.
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor — attaches JWT token from localStorage when available.
 */
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("ahv_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

/**
 * Response interceptor — unwraps the standard `{ status, data }` envelope.
 * After this interceptor, callers receive the inner `data` object directly.
 */
api.interceptors.response.use(
  (response) => {
    if (response.data && response.data.status === "success") {
      // If the response has both data and meta, return the whole object minus status
      if (response.data.meta) {
        const { status, ...rest } = response.data;
        return rest;
      }
      // If data exists, unwrap it. If it doesn't, return the whole object (likely contains a message)
      return response.data.data !== undefined ? response.data.data : response.data;
    }
    return response.data;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const status = error.response?.status;
    const method = originalRequest?.method?.toUpperCase();
    const url = originalRequest?.url;
    
    // Check if it's a 401 error and not the login or refresh endpoint itself
    if (status === 401 && !originalRequest._retry && !url?.includes("/auth/login") && !url?.includes("/auth/refresh")) {
      originalRequest._retry = true;
      
      try {
        // Dynamically import authService to avoid circular dependency
        const { authService } = await import("./authService");
        const newToken = await authService.refreshToken();
        
        // Update the header and retry
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        // Re-execute the original request with the new token
        return api.request(originalRequest);
      } catch (refreshError) {
        // If refresh fails, log out and redirect to login
        const { authService } = await import("./authService");
        await authService.logout();
        if (typeof window !== "undefined") {
          window.location.href = "/admin/login";
        }
        return Promise.reject(refreshError);
      }
    }

    const message =
      (error.response?.data as any)?.message || 
      (error.response?.data as any)?.error ||
      error.message || 
      "An unexpected error occurred";
      
    const errors = (error.response?.data as any)?.errors || [];

    console.error(`[API Error] ${method} ${url} | Status: ${status}`, { 
      message, 
      errors, 
      data: error.response?.data 
    });

    return Promise.reject({
      message,
      errors,
      status,
      originalError: error,
    });
  },

);

export default api;
